import type { ColumnConfig } from '@/components/tables/enhanced-data-table'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import type { MembershipCategory, Requirement } from '@/utils/membership/types'
import { MoreHorizontal, Pencil, Trash2 } from 'lucide-react'

export const createColumns = (
  onEdit: (category: MembershipCategory) => void,
  onDelete: (category: MembershipCategory) => void
): ColumnConfig<MembershipCategory>[] => [
  {
    key: 'categoryName',
    header: 'Category Name',
    sortable: true,
    filterable: true,
    cell: (value) => <div className="font-medium">{value as string}</div>,
  },
  {
    key: 'description',
    header: 'Description',
    sortable: false,
    filterable: true,
    cell: (value) => {
      const plainText = (value as string).replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()
      return (
        <div className="max-w-[300px] truncate text-muted-foreground">
          {plainText}
        </div>
      )
    },
  },
  {
    key: 'annualFee',
    header: 'Annual Fee',
    sortable: true,
    filterable: false,
    type: 'currency',
    cell: (value) => {
      const amount = parseFloat(value as string)
      const formatted = new Intl.NumberFormat('en-UG', {
        style: 'currency',
        currency: 'UGX',
      }).format(amount)
      return <div className="font-medium">{formatted}</div>
    },
  },
  {
    key: 'targetAudience',
    header: 'Target Audience',
    sortable: false,
    filterable: true,
    cell: (value) => (
      <div className="max-w-[200px] truncate text-muted-foreground">
        {value as string || '—'}
      </div>
    ),
  },
  {
    key: 'requirements',
    header: 'Requirements',
    sortable: false,
    filterable: false,
    cell: (_value, row) => {
      const requirements = row.requirements as Requirement[] | undefined

      if (!requirements || requirements.length === 0) {
        return <span className="text-muted-foreground">—</span>
      }

      const mandatoryCount = requirements.filter((requirement) => requirement.mandatory).length

      return (
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center rounded-md bg-secondary px-2 py-0.5 text-xs">
            {requirements.length} total
          </span>
          {mandatoryCount > 0 && (
            <span className="inline-flex items-center rounded-md bg-destructive text-destructive-foreground px-2 py-0.5 text-xs">
              {mandatoryCount} mandatory
            </span>
          )}
        </div>
      )
    },
  },
  {
    key: 'active',
    header: 'Status',
    sortable: true,
    filterable: false,
    cell: (value) => {
      const isActive = value as boolean
      return (
        <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium ${
          isActive ? 'bg-primary text-primary-foreground' : 'bg-secondary text-secondary-foreground'
        }`}>
          {isActive ? 'Active' : 'Inactive'}
        </span>
      )
    },
  },
  {
    key: 'actions',
    header: 'Actions',
    sortable: false,
    filterable: false,
    cell: (_value, row) => {
      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-8 w-8 p-0">
              <span className="sr-only">Open menu</span>
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>Actions</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation()
                onEdit(row)
              }}
            >
              <Pencil className="mr-2 h-4 w-4" />
              Edit
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation()
                onDelete(row)
              }}
              className="text-destructive"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )
    },
  },
]
